import type { CSSProperties } from 'react';

import type { StorefrontTeamMember } from '@/features/storefront/api/team';
import { isoDay, mondayFirst } from '@/features/storefront/sections/bookingSlots';
import type { BookingHours, BookingsSection } from '@/features/storefront/sections/schema';
import { memberFromSearch } from '@/features/storefront/sections/team';

/**
 * The date arithmetic and formatting both shapes of the bookings form share.
 *
 * `BookingCalendar` (the one-page form) and `BookingWizard` (the same booking
 * a step at a time) ask the same questions of the same diary, so which day a
 * strip starts on, what a day's times are called and how a time is printed
 * live here once - two copies would be two places for them to drift apart
 * between shapes a merchant switches with one toggle. The fields and the day
 * strip they share are components, in `bookingParts.tsx`.
 */

/**
 * How many days the strip shows at once.
 *
 * A fortnight rather than the whole diary, which a merchant may open a year
 * ahead: three hundred buttons in a scroller is not a way to find next Tuesday.
 * The rest of the window is reached a fortnight at a time - see `page` - so the
 * setting still means what it says.
 */
export const DAYS_SHOWN = 14;

/** One day, as the strip needs it. */
export interface StripDay {
  iso: string;
  date: Date;
  /** False on a weekday the shop is shut - the button is there but disabled. */
  open: boolean;
}

/**
 * The `Intl` locale the calendar formats in.
 *
 * English keeps the British form it has always printed ("Monday 5 January",
 * no comma); every other storefront language formats in its own locale.
 */
export function calendarLocale(locale: string): string {
  return locale.toLowerCase().startsWith('en') ? 'en-GB' : locale;
}

export function formatTime(iso: string, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(calendarLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }).format(new Date(iso));
}

export function formatDay(iso: string, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(calendarLocale(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(new Date(iso));
}

/**
 * The days the strip offers, and whether the shop opens on each.
 *
 * Built from the reader's own today, which is the honest thing for a strip
 * whose labels are dates: the server decides what a day *means* in the shop's
 * timezone, and disagreeing by an hour either side of midnight costs the
 * shopper nothing, while guessing the shop's date here would show them a
 * Tuesday labelled Monday.
 */
export function stripDays(
  section: BookingsSection,
  from: number,
  /** Whose week decides which days are open: a person's, several people's, or the section's. */
  week: BookingHours[] = section.hours,
  count: number = DAYS_SHOWN,
): StripDay[] {
  const total = section.booking_window_days + 1;
  const length = Math.max(0, Math.min(count, total - from));
  const today = new Date();

  return Array.from({ length }, (_, index) => {
    const offset = from + index;
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
    return { iso: isoDay(date), date, open: weekOpens(week, date) };
  });
}

/** Whether a week has the shop open on a date's weekday. */
export function weekOpens(week: readonly BookingHours[], date: Date): boolean {
  const hours = week.find((row) => row.weekday === mondayFirst(date));
  return Boolean(hours) && !hours?.closed;
}

/**
 * How far into the diary the first open day is, as a page of the strip.
 *
 * A salon that only opens on Wednesdays should show its Wednesdays rather than
 * a fortnight of greyed-out buttons - so the strip starts where the shop does.
 * Nothing open anywhere in the window leaves it at the beginning, which is the
 * honest picture of a section whose hours say the business never opens.
 */
export function firstOpenPage(
  section: BookingsSection,
  week: BookingHours[] = section.hours,
): number {
  const total = section.booking_window_days + 1;
  for (let from = 0; from < total; from += DAYS_SHOWN) {
    if (stripDays(section, from, week).some((day) => day.open)) return from;
  }
  return 0;
}

/** The first open day of a week, as `YYYY-MM-DD`; blank when it never opens. */
export function firstOpenDay(section: BookingsSection, week: BookingHours[]): string {
  const days = stripDays(section, firstOpenPage(section, week), week);
  return (days.find((entry) => entry.open) ?? days[0])?.iso ?? '';
}

/**
 * Who a choose-mode calendar starts on.
 *
 * The person the caller named (a profile embedding its own calendar), else a
 * `?member=` slug from a team card's Book button, wins when that person
 * offers the service; then "any available", where the merchant offers it;
 * then the first person, because a picker with nothing picked and no "any"
 * would be a calendar with no diary to show.
 */
export function initialPerson(
  section: BookingsSection,
  people: readonly StorefrontTeamMember[],
  search: string | null,
  named?: string | null,
): string | null {
  if (section.staff_mode === 'fixed') return section.staff_member_id;
  if (section.staff_mode !== 'choose') return null;
  const slug = named || memberFromSearch(search);
  const linked = slug ? people.find((member) => member.slug === slug) : undefined;
  if (linked) return linked.id;
  if (section.allow_any_staff) return null;
  return people[0]?.id ?? null;
}

/** The look every booking field shares: the shop's own surface, radius and ink. */
export const FIELD_STYLE: CSSProperties = {
  borderColor: 'var(--storefront-surface)',
  borderRadius: 'var(--storefront-radius)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-text)',
};

/**
 * Where a time falls in the shop's day, for the step-by-step form's grouped
 * grid. Read in the shop's own zone - the zone every time is printed in - so
 * a clinic's 11:30 is morning wherever the shopper is reading from.
 */
export type DayPeriod = 'morning' | 'afternoon' | 'evening';

export function dayPeriod(iso: string, timeZone?: string): DayPeriod {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone }).format(
      new Date(iso),
    ),
  );
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/** The longest answer the server takes for each kind of question. */
export const ANSWER_LIMITS = { text: 500, textarea: 2000 } as const;
